/**
 * Scroll after React commit + layout. Retries if the node is not mounted yet
 * and once more after late-growing content above the target.
 */
export function queueSmoothScrollToId(id: string): void {
  let tries = 0;
  const run = () => {
    const el = document.getElementById(id);
    if (!el) {
      if (tries < 24) {
        tries += 1;
        window.setTimeout(run, 50);
      }
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 400);
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
}

/** Current document scroll (window or root element). */
export function readWindowScrollY(): number {
  return (
    window.scrollY
    || document.documentElement.scrollTop
    || document.body.scrollTop
    || 0
  );
}

/**
 * Restore window scroll after layout remounts (e.g. closing immersive product).
 * Retries while the document grows back (hero / grid images).
 */
export function restoreWindowScrollY(
  y: number,
  opts?: { attempts?: number; intervalMs?: number },
): void {
  const target = Math.max(0, Math.round(y));
  const attempts = opts?.attempts ?? 24;
  const intervalMs = opts?.intervalMs ?? 50;
  let n = 0;
  const run = () => {
    window.scrollTo({ top: target, left: 0, behavior: 'auto' });
    n += 1;
    if (n >= attempts) return;
    if (Math.abs(readWindowScrollY() - target) > 4) {
      window.setTimeout(run, intervalMs);
    }
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
}

/** Layout gap taken by classic scrollbar and/or `scrollbar-gutter: stable`. */
function layoutScrollbarGap(html: HTMLElement = document.documentElement): number {
  return Math.max(0, window.innerWidth - html.clientWidth);
}

type ScrollLockSnapshot = {
  htmlOverflow: string;
  bodyOverflow: string;
  bodyPaddingRight: string;
};

let scrollLockCount = 0;
let scrollLockSnapshot: ScrollLockSnapshot | null = null;

/**
 * Lock page scroll without a horizontal jump.
 * Nested callers (splash + product overlay) share one lock via ref-count —
 * unlocking must not restore `overflow: hidden` from an outer lock.
 * Relies on `scrollbar-gutter: stable` on the website shell; only pads when
 * `overflow: hidden` actually collapses that lane.
 */
export function lockDocumentScroll(): () => void {
  const html = document.documentElement;
  const body = document.body;

  if (scrollLockCount === 0) {
    scrollLockSnapshot = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPaddingRight: body.style.paddingRight,
    };

    const gapBefore = layoutScrollbarGap(html);

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';

    const lost = Math.max(0, gapBefore - layoutScrollbarGap(html));
    if (lost > 0) {
      const currentPad = Number.parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${currentPad + lost}px`;
    }
  }

  scrollLockCount += 1;
  let released = false;

  return () => {
    if (released) return;
    released = true;
    if (scrollLockCount <= 0) return;
    scrollLockCount -= 1;
    if (scrollLockCount > 0 || !scrollLockSnapshot) return;

    html.style.overflow = scrollLockSnapshot.htmlOverflow;
    body.style.overflow = scrollLockSnapshot.bodyOverflow;
    body.style.paddingRight = scrollLockSnapshot.bodyPaddingRight;
    scrollLockSnapshot = null;
  };
}

type DialogScrollSnapshot = {
  scrollY: number;
  htmlOverflow: string;
  bodyOverflow: string;
  bodyPosition: string;
  bodyTop: string;
  bodyWidth: string;
};

let dialogLockCount = 0;
let dialogLockSnapshot: DialogScrollSnapshot | null = null;

/** Drop a leftover fixed body left by two dialogs closing out of order. */
export function releaseStuckDialogScroll() {
  if (dialogLockCount > 0) return;
  const body = document.body;
  if (body.style.position !== 'fixed') return;
  const y = Math.abs(Number.parseInt(body.style.top, 10) || 0);
  body.style.position = '';
  body.style.top = '';
  body.style.width = '';
  body.style.overflow = '';
  document.documentElement.style.overflow = '';
  window.scrollTo({ top: y, left: 0, behavior: 'auto' });
}

/**
 * Several dialogs may lock the page. Only the first one remembers the real
 * scroll; the last one to close puts it back.
 */
export function lockDialogScroll(): () => void {
  releaseStuckDialogScroll();
  const html = document.documentElement;
  const body = document.body;
  if (dialogLockCount === 0) {
    const scrollY = readWindowScrollY();
    dialogLockSnapshot = {
      scrollY,
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
  }
  dialogLockCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (dialogLockCount <= 0) return;
    dialogLockCount -= 1;
    if (dialogLockCount > 0 || !dialogLockSnapshot) return;
    const snapshot = dialogLockSnapshot;
    dialogLockSnapshot = null;
    html.style.overflow = snapshot.htmlOverflow;
    body.style.overflow = snapshot.bodyOverflow;
    body.style.position = snapshot.bodyPosition;
    body.style.top = snapshot.bodyTop;
    body.style.width = snapshot.bodyWidth;
    restoreWindowScrollY(snapshot.scrollY, { attempts: 12, intervalMs: 32 });
  };
}
